import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Grid3x3, Baby, ShoppingCart, User } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Badge } from '../ui/badge';

/**
 * Mobile-only bottom navigation bar
 * Sticky at bottom, thumb-friendly, 5 key actions
 */
export const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount, user } = useCart();

  // Hide on certain pages
  const hiddenRoutes = ['/checkout', '/admin', '/order-success'];
  const shouldHide = hiddenRoutes.some(route => location.pathname.startsWith(route));

  if (shouldHide) return null;

  const navItems = [
    { icon: Home, label: 'Home', path: '/', testId: 'bottom-nav-home' },
    { icon: Grid3x3, label: 'Categories', path: '/categories', testId: 'bottom-nav-categories' },
    { icon: Baby, label: 'Age', path: '/shop/age', testId: 'bottom-nav-age' },
    { icon: ShoppingCart, label: 'Cart', path: '/cart', badge: cartCount, testId: 'bottom-nav-cart' },
    { icon: User, label: user ? 'Account' : 'Login', path: user ? '/orders' : '/cart', testId: 'bottom-nav-account' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav 
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border shadow-lg"
      data-testid="bottom-navigation"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={
                `flex flex-col items-center justify-center flex-1 h-full space-y-1 transition-colors relative
                ${active ? 'text-cyan-600' : 'text-muted-foreground'}
                active:scale-95 touch-manipulation`
              }
              data-testid={item.testId}
            >
              <div className="relative">
                <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
                {item.badge !== undefined && item.badge > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 min-w-[1.25rem] flex items-center justify-center px-1 text-[10px] font-bold rounded-full bg-red-500 text-white border-2 border-white">
                    {item.badge > 99 ? '99+' : item.badge}
                  </Badge>
                )}
              </div>
              <span 
                className={
                  `text-[10px] font-medium
                  ${active ? 'text-cyan-600' : 'text-muted-foreground'}`
                }
              >
                {item.label}
              </span>
              {active && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-cyan-600 rounded-b-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
